import { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  MenuItem, 
  Select 
} from "@mui/material";

import TasksSection from "./TasksSection";
import ValuesSection from "./ValuesSection";
import AdditionalCommentsSection from "./AdditionalCommentsSection";
import SuggestedFeedbackSection from "./SuggestedFeedbackSection";
import NewObjectivesSection from "./NewObjectivesSection";
import ObjectivesSection from "./ObjectivesSection";
import { apiUrl } from "../../apiBase";

export default function EvaluationForm() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [objectives, setObjectives] = useState([]);
  const [objectivesErrors, setObjectivesErrors] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tasksErrors, setTasksErrors] = useState([]);
  const [values, setValues] = useState([]);
  const [valuesErrors, setValuesErrors] = useState([]);
  const [additionalComments, setAdditionalComments] = useState("");
  const [suggestedFeedback, setSuggestedFeedback] = useState("");
  const [suggestedAreasOfImprovement, setSuggestedAreasOfImprovement] = useState("");
  const [score, setScore] = useState("");
  const [newObjectives, setNewObjectives] = useState([]);
  const [feedbackConfirmed, setFeedbackConfirmed] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState(null);


  const totalSteps = 8;

  useEffect(() => {
    async function fetchEmployees() {
      try {
        const response = await fetch(apiUrl("/api/empleados"));
        if (!response.ok) {
          throw new Error("Error al cargar empleados");
        }
        const data = await response.json();
        setEmployees(data);
      } catch (error) {
        console.error("❌ Error trayendo empleados:", error);
      }
    }
    fetchEmployees();
  }, []); 

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedEmployee) return;

      try {
        const response = await fetch(apiUrl(`/api/objectives/${selectedEmployee.legajo}`));
        if (response.ok) {
          const rawObjectives = await response.json();
          setObjectives((Array.isArray(rawObjectives) ? rawObjectives : []).map((obj) => ({
            ...obj,
            achievement: obj.achievement ?? "",
            comment: obj.comment ?? "",
          })));
        }
      } catch (error) {
        console.error("Error trayendo objetivos:", error);
      }

      try {
        const tasksResponse = await fetch("/tasks.json");
        if (tasksResponse.ok) {
          const tasksData = await tasksResponse.json();
          setTasks(tasksData.map((task) => ({
            ...task,
            completion: "",
            comment: "",
            rating: task.rating ?? "",
          })));
        }
      } catch (error) {
        console.error("Error trayendo tareas:", error);
      }

      try {
        const valuesResponse = await fetch("/values.json");
        if (valuesResponse.ok) {
          const valuesData = await valuesResponse.json();
          setValues(valuesData.map((value) => ({
            ...value,
            rating: value.rating ?? "",
          })));
        }
      } catch (error) {
        console.error("Error trayendo valores:", error);
      }
    };

    fetchData();
  }, [selectedEmployee]);
  

  useEffect(() => {
    const timeout = setTimeout(() => {
      saveDraft();
    }, 30000);

    return () => clearTimeout(timeout);
  }, [objectives, tasks, values, additionalComments, suggestedFeedback, suggestedAreasOfImprovement, score, newObjectives]);

  useEffect(() => {
    if (currentStep === 5) {
      generateFeedbackFromAI();
    }
  }, [currentStep]);

  async function saveDraft() {
    setIsSaving(true);
    await fetch(apiUrl("/api/save-draft"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        selectedEmployee,
        objectives,
        tasks,
        values,
        additionalComments,
        suggestedFeedback,
        suggestedAreasOfImprovement,
        score,
        newObjectives,
      }),
    });
    setIsSaving(false);
  }

  useEffect(() => {
    const fetchPeriodo = async () => {
      try {
        const response = await fetch(apiUrl("/api/periodo-actual"));
        const data = await response.json();
        setCurrentPeriod(data.periodo);
      } catch (error) {
        console.error("❌ Error al traer el período actual:", error);
      }
    };
  
    fetchPeriodo();
  }, []);
  
  async function generateFeedbackFromAI() {
    try {
      setIsGeneratingFeedback(true);
  
      const response = await fetch(apiUrl("/api/generate-feedback"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tasks,
          values,
          objectives,
          additionalComments,
        }),
      });
  
      const data = await response.json();
  
      console.log("Respuesta del backend:", data);
      console.log("Tareas:", tasks);
      console.log("Valores:", values);
      console.log("Objetivos:", objectives);
  
      if (response.ok && data.feedback) {
        const partes = data.feedback.split(/\n\s*\n/);
        setSuggestedFeedback(partes[0] || "");
        setSuggestedAreasOfImprovement(partes[1] || "");
  
        const taskRatings = tasks.map(t => parseFloat(t.rating)).filter(n => !isNaN(n));
        const valueRatings = values.map(v => parseFloat(v.rating)).filter(n => !isNaN(n));
        const objectiveRatings = objectives
          .map(o => typeof o.achievement === "number" ? o.achievement / 20 : parseFloat(o.achievement) / 20)
          .filter(n => !isNaN(n));
  
        const allRatings = [...taskRatings, ...valueRatings, ...objectiveRatings];
  
        console.log("taskRatings:", taskRatings);
        console.log("valueRatings:", valueRatings);
        console.log("objectiveRatings:", objectiveRatings);
        console.log("Promedio calculado:", allRatings.length > 0 ? allRatings.reduce((acc, val) => acc + val, 0) / allRatings.length : "No hay datos");
  
        if (allRatings.length > 0) {
          const avg = allRatings.reduce((acc, val) => acc + val, 0) / allRatings.length;
          setScore(Math.round(avg));
        }
      } else {
        console.error("Error en la respuesta del servidor:", data.error);
      }
    } catch (error) {
      console.error("Error al generar el feedback:", error);
    } finally {
      setIsGeneratingFeedback(false);
    }
  }
  

  function handleSubmitToHR() {
    if (newObjectives.length === 0) {
      alert("Debés cargar al menos un nuevo objetivo para poder enviar la evaluación a HR.");
      return;
    }
    console.log("Enviar a HR");
  }

  function handlePublish() {
    if (!feedbackConfirmed) {
      alert("Debés confirmar que brindaste el feedback al colaborador antes de publicar la evaluación.");
      return;
    }
    console.log("Publicar Evaluación");
  }

  function validateCurrentStep() {
    if (currentStep === 0) {
      if (!selectedEmployee) return false;
    }

    if (currentStep === 1) {
      const newErrors = [];
      objectives.forEach((obj, index) => {
        if (obj.achievement === "" || obj.achievement === undefined || String(obj.comment ?? "").trim() === "") {
          newErrors.push(index);
        }
      });
      setObjectivesErrors(newErrors);
      if (newErrors.length > 0) return false;
    }

    if (currentStep === 2) {
      const newErrors = [];
      tasks.forEach((task, index) => {
        if (!task.rating) {
          newErrors.push(index);
        }
      });
      setTasksErrors(newErrors);
      if (newErrors.length > 0) return false;
    }

    if (currentStep === 3) {
      const newErrors = [];
      values.forEach((val, index) => {
        if (!val.rating) {
          newErrors.push(index);
        }
      });
      setValuesErrors(newErrors);
      if (newErrors.length > 0) return false;
    }

    return true;
  }

  return (
    <Box sx={{ padding: 4 }}>
      <Typography variant="h4" mb={1}>Evaluación de Desempeño</Typography>
      <Typography variant="subtitle1" mb={3}>Paso {currentStep + 1} de {totalSteps}</Typography>

      {selectedEmployee && (
  <Typography variant="subtitle2" mb={2}>
    Evaluando a: {selectedEmployee.nombre} {selectedEmployee.apellido} — {selectedEmployee.posicion}
  </Typography>
)}

      {currentStep === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Seleccioná a quién querés evaluar
          </Typography>
          <Select
            value={selectedEmployee ? String(selectedEmployee.legajo) : ""}
            onChange={(e) => {
              const selected = employees.find((emp) => String(emp.legajo) === String(e.target.value));
              setSelectedEmployee(selected);
            }}
            displayEmpty
            fullWidth
          >
            <MenuItem value="" disabled>
              Seleccionar colaborador
            </MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.legajo} value={String(emp.legajo)}>
                {`${emp.nombre} ${emp.apellido} — ${emp.posicion}`}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {currentStep === 1 && (
        <ObjectivesSection
        objectives={objectives}
        setObjectives={setObjectives}
        objectivesErrors={objectivesErrors}
        setObjectivesErrors={setObjectivesErrors}
        selectedEmployee={selectedEmployee}
        currentPeriod={currentPeriod}
      />      
      )}
      {currentStep === 2 && (
        <TasksSection 
          tasks={tasks} 
          setTasks={setTasks} 
          tasksErrors={tasksErrors}
          setTasksErrors={setTasksErrors}
        />
      )}
      {currentStep === 3 && (
        <ValuesSection 
          values={values}
          setValues={setValues}
          valuesErrors={valuesErrors}
          setValuesErrors={setValuesErrors}
        />
      )}
      {currentStep === 4 && (
        <AdditionalCommentsSection 
          comments={additionalComments}
          setComments={setAdditionalComments}
        />
      )}
      {currentStep === 5 && (
        <SuggestedFeedbackSection 
          suggestedFeedback={suggestedFeedback}
          setSuggestedFeedback={setSuggestedFeedback}
          suggestedAreasOfImprovement={suggestedAreasOfImprovement}
          setSuggestedAreasOfImprovement={setSuggestedAreasOfImprovement}
          score={score}
          setScore={setScore}
          isGeneratingFeedback={isGeneratingFeedback}
        />
      )}
      {currentStep === 6 && (
  <NewObjectivesSection
    newObjectives={newObjectives}
    setNewObjectives={setNewObjectives}
    onNextStep={() => setCurrentStep(7)} 
  />
)}
      {currentStep === 7 && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={feedbackConfirmed}
                onChange={(e) => setFeedbackConfirmed(e.target.checked)}
              />
            }
            label="Confirmo que brindé el feedback al colaborador"
          />
          <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
            <Button variant="outlined" onClick={saveDraft} disabled={isSaving}>
              {isSaving ? "Guardando..." : "Guardar Borrador"}
            </Button>
            <Button variant="contained" onClick={handleSubmitToHR} disabled={newObjectives.length === 0}>
              Enviar a HR
            </Button>
            <Button variant="contained" onClick={handlePublish} disabled={!feedbackConfirmed}>
              Publicar Evaluación
            </Button>
          </Box>
        </>
      )}

      <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
        {currentStep > 0 && (
          <Button variant="outlined" onClick={() => setCurrentStep(currentStep - 1)}>
            Anterior
          </Button>
        )}
        {currentStep < 7 && (
          <Button variant="contained" onClick={() => {
            if (validateCurrentStep()) {
              setCurrentStep(currentStep + 1);
            } else {
              alert("Por favor completá todos los campos obligatorios antes de avanzar.");
            }
          }}>
            Siguiente
          </Button>
        )}
      </Box>
    </Box>
  );
}